import { Card, ContentState } from "@whitehaven/ui";

const assurances = [
  {
    icon: "shield",
    label: "Private by design",
    title: "Protected account access",
    detail:
      "Personal appointments, forms, invoices, and documents never appear on public pages.",
  },
  {
    icon: "people",
    label: "Purposeful access",
    title: "The right view for each role",
    detail:
      "Guests, staff, and administrators see only the tools and information approved for them.",
  },
  {
    icon: "spark",
    label: "Clear next steps",
    title: "Simple, readable guidance",
    detail:
      "Every prompt explains what is happening, what you can do now, and where to get help.",
  },
] as const;

function AssuranceIcon({ name }: { readonly name: string }) {
  if (name === "people") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="8" cy="8" r="3" />
        <circle cx="17" cy="8" r="3" />
        <path d="M2 20a6 6 0 0 1 12 0M12 20a5 5 0 0 1 10 0" />
      </svg>
    );
  }

  if (name === "spark") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m12 3 1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z" />
        <path d="m18.5 14 .8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function PortalHomeRoute() {
  return (
    <div className="portal-page">
      <section className="portal-page-head">
        <div>
          <p className="portal-kicker">Secure account portal</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            Welcome to your Whitehaven portal.
          </h1>
          <p className="portal-page-head__description">
            Private appointments, forms, messages, invoices, and released
            documents will be organized here.
          </p>
        </div>
        <span className="portal-page-head__badge">
          <span aria-hidden="true" /> Secure by design
        </span>
      </section>

      <section className="portal-entry-grid" aria-label="Portal access status">
        <div className="portal-entry-grid__copy">
          <ContentState
            className="portal-access-state"
            kind="empty"
            label="Private account access"
            icon={<span className="portal-access-state__lock" />}
            title="Sign-in access is not open yet."
            live={false}
            actions={
              <>
                <a href="https://whitehavenwellness.com/">
                  Return to Whitehaven <span aria-hidden="true">→</span>
                </a>
                <a
                  className="portal-access-state__secondary"
                  href="mailto:info@whitehavenwellness.com"
                >
                  Email our team
                </a>
              </>
            }
          >
            <p>
              Whitehaven is completing privacy and access checks before portal
              invitations are activated. No action is needed yet.
            </p>
          </ContentState>
          <p className="portal-access-note">
            Please do not send private health, payment, or report information by
            email.
          </p>
        </div>

        <Card
          as="aside"
          className="portal-journey"
          eyebrow="Private by design"
          title="A clearer path to your private information."
          description="Once access is enabled, approved account tools stay together in one secure place."
          media={
            <div className="portal-journey__scene" aria-hidden="true">
              <span className="portal-journey__sun" />
              <span className="portal-journey__leaf">
                {Array.from({ length: 10 }, (_, index) => (
                  <i key={index} />
                ))}
              </span>
              <span className="portal-journey__wave" />
              <span className="portal-journey__status">Private</span>
            </div>
          }
        >
          <ol className="portal-journey__steps">
            <li>
              <span>1</span>
              <div>
                <strong>Receive an invitation</strong>
                <small>Access begins only after approval</small>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Sign in securely</strong>
                <small>Your identity and role are checked</small>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>Open your portal</strong>
                <small>See only information approved for you</small>
              </div>
            </li>
          </ol>
        </Card>
      </section>

      <section
        className="portal-assurance-grid"
        aria-label="Whitehaven portal principles"
      >
        {assurances.map((item) => (
          <article key={item.label}>
            <span className="portal-assurance-grid__icon">
              <AssuranceIcon name={item.icon} />
            </span>
            <div>
              <p>{item.label}</p>
              <h2>{item.title}</h2>
              <p>{item.detail}</p>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
